import { useMutation, useQuery } from "@tanstack/react-query";
import { ApiError, apiFetch } from "@/lib/api";
import { keys } from "@/hooks/keys";
import { API_BASE } from "@/lib/utils";
import { useAuth } from "@/lib/auth";

/** One home-screen chip. `label` is what fits the 34px pill; `prompt` is what
 *  the orchestrator receives when it is clicked. */
export type OrgSuggestion = { label: string; prompt: string };

type SuggestionsPayload = {
  suggestions?: unknown;
};

/** The stored set predates the {label, prompt} split, so a plain string still
 *  reads back — as a chip whose label and prompt are the same text. */
function normalizeSuggestion(raw: unknown): OrgSuggestion | null {
  if (typeof raw === "string") {
    const s = raw.trim();
    return s ? { label: s, prompt: s } : null;
  }
  if (!raw || typeof raw !== "object") return null;
  const o = raw as { label?: unknown; prompt?: unknown };
  const label = typeof o.label === "string" ? o.label.trim() : "";
  const prompt = typeof o.prompt === "string" ? o.prompt.trim() : "";
  if (!label && !prompt) return null;
  return { label: label || prompt, prompt: prompt || label };
}

type OnboardingStatus = {
  companyDomain?: string;
  agentOnboardingDomain?: string;
  isPersonalEmail?: boolean;
  /** True when the org has finished onboarding (backend status = "completed"). */
  completed?: boolean;
};

/**
 * The four personalized home-screen suggestions for the caller's org.
 * Returns an empty array when nobody in the org has onboarded, or when the
 * background generation hasn't produced a set yet — callers fall back to a
 * hardcoded default in that case.
 *
 * The GET triggers server-side regeneration (fire-and-forget) when the stored
 * set is missing or stale, so we poll on a short interval while it is still
 * empty and the fresh set appears without a manual reload.
 */
export function useOrgSuggestions() {
  const { userContext } = useAuth();
  const orgId = userContext?.orgId;

  return useQuery<OrgSuggestion[]>({
    queryKey: keys.onboarding.suggestions(orgId),
    queryFn: async () => {
      try {
        const data = await apiFetch<SuggestionsPayload>(`${API_BASE}/api/v1/me/suggestions`);
        const raw = data?.suggestions;
        if (!Array.isArray(raw)) return [];
        return raw.map(normalizeSuggestion).filter((s): s is OrgSuggestion => s !== null);
      } catch (err) {
        // 403 is a below-operator caller, who never renders these anyway.
        if (err instanceof ApiError && (err.status === 404 || err.status === 403)) return [];
        throw err;
      }
    },
    staleTime: 60 * 1000,
    retry: false,
    // BI4: this used to poll every 8s forever until it got exactly 4
    // suggestions — if the background generation never produces them (e.g.
    // no AI Gateway credential configured), the caller's loading skeleton
    // never resolves, showing as "possibly stuck" indefinitely. Capping at
    // 5 attempts (~40s) lets the caller fall back to generic examples.
    refetchInterval: (query) => {
      const data = query.state.data;
      if (Array.isArray(data) && data.length === 4) return false;
      if (query.state.dataUpdateCount >= 5) return false;
      return 8000;
    },
    refetchIntervalInBackground: false,
  });
}

export function useOnboardingStatus() {
  return useQuery<OnboardingStatus>({
    queryKey: keys.onboarding.status(),
    queryFn: async () => {
      try {
        const data = await apiFetch<OnboardingStatus>(`${API_BASE}/api/v1/onboarding/status`);
        return data ?? {};
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) return {};
        throw err;
      }
    },
    staleTime: 60_000,
    retry: false,
  });
}

export function useConfirmOnboardingDomain() {
  return useMutation({
    mutationFn: (domain: string) =>
      apiFetch(`${API_BASE}/api/v1/onboarding/agent-domain`, {
        method: "POST",
        body: JSON.stringify({ domain }),
      }),
  });
}
